/**
 * Operator-facing popups and toasts (user refresh, errors, status).
 */

export type OperatorNotifyType = "success" | "error" | "warning" | "info";

export interface OperatorNotifyDetail {
  id?: string;
  title: string;
  message: string;
  type?: OperatorNotifyType;
  /** Centered modal (user actions). Default true for explicit refresh flows. */
  modal?: boolean;
  closable?: boolean;
  /** Ms until auto-dismiss; 0 = until user closes. */
  duration?: number;
}

export const OPERATOR_NOTIFY_EVENT = "ead-operator-notify";

export function notifyOperator(detail: OperatorNotifyDetail): string {
  const id = detail.id || `op-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  window.dispatchEvent(
    new CustomEvent(OPERATOR_NOTIFY_EVENT, {
      detail: {
        ...detail,
        id,
        type: detail.type || "info",
        modal: detail.modal !== false,
        closable: detail.closable !== false,
        duration: detail.duration ?? 0,
      } satisfies OperatorNotifyDetail & { id: string },
    }),
  );
  return id;
}

/** Show operator notice only when detail is non-null (e.g. skip silent refresh outcomes). */
export function notifyPfmRefreshResult(snap: Parameters<typeof formatPfmRefreshResult>[0]): void {
  const detail = formatPfmRefreshResult(snap);
  if (detail) notifyOperator(detail);
}

export function notifyPfmBackfillResult(result: Parameters<typeof formatPfmBackfillResult>[0]): void {
  const detail = formatPfmBackfillResult(result);
  if (detail) notifyOperator(detail);
}

export function formatPfmRefreshResult(snap: {
  ok?: boolean;
  code?: string;
  message?: string;
  pfmGeneration?: number;
  pfm_generation?: number;
  pfmRevision?: number;
  pfm_revision?: number;
}): OperatorNotifyDetail | null {
  const code = String(snap.code || "").trim();
  const msg = String(snap.message || "").trim();
  const gen = Number(snap.pfmGeneration ?? snap.pfm_generation ?? 0);
  const rev = Number(snap.pfmRevision ?? snap.pfm_revision ?? 0);

  if (snap.ok === false && code !== "throttled" && code !== "queued" && code !== "queued_deferred") {
    return {
      title: "Refresh failed",
      message: msg || code || "Could not refresh the EAD feature map.",
      type: "error",
      modal: true,
    };
  }
  if (
    code === "no_changes"
    || code === "no_delivery_files"
    || code === "awaiting_delivery"
    || code === "fmr_reports_synced_no_pfm"
    || code === "materialized"
    || code === "queued"
    || code === "queued_deferred"
    || code === "throttled"
  ) {
    if (code === "awaiting_delivery" || code === "no_delivery_files" || code === "fmr_reports_synced_no_pfm") {
      return {
        title: "Refresh",
        message:
          msg
          || "No NewMapAndNodeReport delivery for this run yet. Keeping Previous EAD Feature Map and Node Report.",
        type: "info",
        modal: false,
        duration: 8000,
      };
    }
    return null;
  }
  return {
    title: "Refresh",
    message: msg || code || "Request sent.",
    type: "info",
    modal: false,
    duration: 5000,
  };
}

export function formatPfmBackfillResult(result: {
  ok?: boolean;
  code?: string;
  message?: string;
  processed?: number;
  failed?: number;
  count?: number;
  runs?: Array<{ executionId?: string; execution_id?: string; name?: string; targetGeneration?: number; target_generation?: number; reason?: string }>;
}): OperatorNotifyDetail | null {
  const code = String(result.code || "").trim();
  const msg = String(result.message || "").trim();
  const processed = Number(result.processed ?? 0);
  const failed = Number(result.failed ?? 0);

  if (result.ok === false && code !== "backfill_partial") {
    return {
      title: "Backfill failed",
      message: msg || code || "Could not backfill PFM versions.",
      type: "error",
      modal: true,
    };
  }
  if (code === "nothing_to_do") {
    return null;
  }
  if (code === "backfill_complete" || code === "backfill_partial") {
    const partial = code === "backfill_partial" || failed > 0;
    return {
      title: partial ? "Backfill partially complete" : "Backfill complete",
      message: msg || `Materialized ${processed} run(s).${failed > 0 ? ` ${failed} failed.` : ""}`,
      type: partial ? "warning" : "success",
      modal: true,
    };
  }
  if (typeof result.count === "number") {
    const runs = result.runs || [];
    const lines =
      runs.length === 0
        ? "No runs need backfill."
        : runs
            .slice(0, 8)
            .map((r) => {
              const id = String(r.executionId ?? r.execution_id ?? "").slice(0, 8);
              const gen = r.targetGeneration ?? r.target_generation ?? "?";
              return `• ${r.name || id} → V${gen} (${r.reason || "pending"})`;
            })
            .join("\n");
    const more = runs.length > 8 ? `\n…and ${runs.length - 8} more.` : "";
    if (result.count === 0) {
      return null;
    }
    return {
      title: `${result.count} run(s) need backfill`,
      message: `${lines}${more}`,
      type: "warning",
      modal: false,
      duration: 8000,
    };
  }
  return {
    title: "Backfill",
    message: msg || code || "Done.",
    type: "info",
    modal: true,
  };
}
